import { DrawingLayer } from 'business/models/DrawingLayer';
import {
  ConcentrationVaryAlongCustomDTO,
  GridIngredientDTO,
  GridLayerDTO,
  LayerDTO,
  LayerStateDTO,
  LayerTypeDTO,
  PhVaryAlongCustomDTO,
} from 'business/models/Models';
import {
  getErroneusDropLayers,
  getErroneusWellLayers,
  getFragmentLayersCountInDrop,
  getProteinLayersCountInDrop,
  getSeedLayersCountInDrop,
} from 'business/plateviewer/Generate/GenerateCommon';
import { getLinkedLayerGridDto } from 'business/plateviewer/GenerateUtil';
import { dirtifyOverLappingLayers } from 'business/plateviewer/layer/ResizeLayerTools';
import {
  Drop,
  Plate,
  DropImageParameters,
  WellNavigationDetails,
} from 'components/Experiment/constants';
import {
  GetIngredients,
  LayerType,
  getIngIngType,
  getNewId,
  isNullOrUndefined,
} from 'utils/Global';

export function validateWellsAndDrops(
  plates: Plate[],
  containerMaxDropVolume: number,
  layers: LayerDTO[],
) {
  plates.forEach((plate) => {
    if (plate.ingredients) {
      const errorsInWells = [];
      plate.ingredients.wellList.forEach((well) => {
        const erroneousWellLayers = getErroneusWellLayers(well);
        const erroneousDropLayers = getErroneusDropLayers(well);
        let errorFoundInWell = false;
        if (
          well.wellVolume > plate.ingredients.maxWellVolume ||
          erroneousWellLayers.length > 0 ||
          erroneousDropLayers.length > 0 ||
          well.wellWaterVolume < 0
        ) {
          let error = '';
          if (erroneousWellLayers.length > 0) {
            error = erroneousWellLayers[0].error;
          } else if (erroneousDropLayers.length > 0) {
            error = erroneousDropLayers[0].error;
          } else if (well.wellVolume > plate.ingredients.maxWellVolume) {
            error = 'Well volume is greater than maximum well volume.';
          } else if (well.wellWaterVolume < 0) {
            error = 'Well has invalid concentrations.';
          }

          well.hasError = true;
          well.error = error;
          errorsInWells.push(error);
          errorFoundInWell = true;
        } else {
          well.dropList.forEach((drop: Drop) => {
            let error = '';
            if (drop.volume > containerMaxDropVolume) {
              error = 'Drop' + ' has invalid volume/concentration.';
              well.hasError = true;
              well.error = error;
              errorsInWells.push(error);
              errorFoundInWell = true;
            } else if (
              layers &&
              (getProteinLayersCountInDrop(drop, layers) > 1 ||
                getSeedLayersCountInDrop(drop, layers) > 1 ||
                getFragmentLayersCountInDrop(drop, layers) > 1)
            ) {
              error =
                'Multiple Protein, Seed, or Fragment Layers found in Drop ' +
                drop.dropNumber +
                '.';

              well.hasError = true;
              well.error = error;
              errorsInWells.push(error);
              errorFoundInWell = true;
            }
            if (drop.dropLayerList) {
              const fragmentLayers = layers.filter(
                (layer) => layer.type === LayerType.Fragment,
              );
              const fragmentLayerIds = fragmentLayers.map((fragment) => fragment.id);
              const dropFragmentLayers = drop.dropLayerList.filter((dropLayer) =>
                fragmentLayerIds.includes(dropLayer.layerId),
              );

              if (
                dropFragmentLayers?.length &&
                dropFragmentLayers[0].dropLayerFragmentList?.length
              ) {
                drop.isFragmentDrop = true;
              }
            }
          });
        }

        if (!errorFoundInWell) {
          well.hasError = false;
          well.error = '';
        }
      });
      if (errorsInWells.length > 0) {
        plate.ingredients.hasError = true;
        plate.ingredients.error = errorsInWells[0];
      } else {
        plate.ingredients.hasError = false;
        plate.ingredients.error = '';
      }
    }
  });
}

export const makeNewParentLinkedLayer = (
  linkedLayers: LayerDTO[],
  layerDetail: GridLayerDTO,
) => {
  for (let i = 0; i < linkedLayers.length; i++) {
    if (
      (linkedLayers[i].layerDetail as GridLayerDTO).subGridNumber >
      layerDetail.subGridNumber
    ) {
      (linkedLayers[i].layerDetail as GridLayerDTO).subGridNumber--;
    }
    if (linkedLayers[i].id > 0) {
      linkedLayers[i].layerState = LayerStateDTO.Modified;
    }
    if (
      (linkedLayers[i].layerDetail as GridLayerDTO).subGridNumber == 1 &&
      !isNullOrUndefined(layerDetail.grid)
    ) {
      (linkedLayers[i].layerDetail as GridLayerDTO).grid = layerDetail.grid;
    }
  }
};

export const getNewSelectedLayerAfterLayerDeletion = (
  linkedLayers: LayerDTO[],
  newLayers: LayerDTO[],
  deletedLayer: LayerDTO,
  delIndex: number,
) => {
  if (isNullOrUndefined(linkedLayers) || newLayers.length === 0) {
    return;
  }
  linkedLayers = linkedLayers.filter((lyr) => lyr.id !== deletedLayer.id);
  if (linkedLayers.length > 0) {
    return linkedLayers[0].id;
  } else {
    if (delIndex > 0) {
      return newLayers[delIndex - 1].id;
    } else {
      return newLayers[0].id;
    }
  }
};

export const applyDoeIngredientsData = (
  expData,
  selectedLayerId,
  doePreviewTableData: any,
) => {
  const _layers = expData.layers;
  const _selectedLayerId = selectedLayerId;
  const _selectedLayer = _layers.find((l) => l.id === _selectedLayerId);

  let selectedLayerDetails;

  if (_selectedLayer.type === LayerTypeDTO.Grid) {
    selectedLayerDetails = _selectedLayer.layerDetail as GridLayerDTO;
    const gridDto = getLinkedLayerGridDto(_layers as LayerDTO[], selectedLayerDetails);

    selectedLayerDetails.grid = gridDto;

    for (const tableIngredient of doePreviewTableData) {
      const addedIngredient = tableIngredient.matchedIngredient;

      {
        const selectIngIngType = getIngIngType(addedIngredient.selectedIngredient.id);

        const ingIngType = GetIngredients().ingredientIngredientType.find(
          (ingingtype) => ingingtype.ingredientId === selectIngIngType.ingredientId,
        );

        const ing = selectedLayerDetails.grid.gridIngredient.find(
          (x) => x.ingredientStockId == addedIngredient.selectedStock.id,
        );

        if (!ing) {
          const gridIngredient: GridIngredientDTO = new GridIngredientDTO({
            id: getNewId(),
            gridId: gridDto.id,
            ingredientIngredientTypeId: ingIngType.id,

            ingredientStockId: addedIngredient.selectedStock.id,

            highPhingredientStockId: isNullOrUndefined(
              addedIngredient.selectedHighpHStock,
            )
              ? null
              : addedIngredient.selectedHighpHStock.id,

            startConcentration: addedIngredient.selectedStock.lowConcentration,
            stopConcentration: addedIngredient.selectedStock.highConcentration,
            concentrationVaryAlong: 6,
            startpH: addedIngredient.selectedStock.stockPh,
            stoppH: addedIngredient.highPhStock
              ? addedIngredient.selectedHighpHStock.stockPh
              : null,
            pHvaryAlong: 6,
          });

          if (isNullOrUndefined(gridDto.gridIngredient)) {
            gridDto.gridIngredient = [];
            gridDto.gridIngredient.push(gridIngredient);

            const deleteIndex = _layers.findIndex(
              (layer) => layer.id === _selectedLayerId,
            );
            _layers.splice(deleteIndex, 1);
            if (deleteIndex != -1) {
              _layers.push(_selectedLayer);
            }
          } else {
            const layerColumns = _selectedLayer.width + 1;
            const layerRows = _selectedLayer.height + 1;

            const matchedIngredientTableRow = doePreviewTableData.find(
              (x) =>
                x.matchedIngredient.selectedIngredient.id ==
                addedIngredient.selectedIngredient.id,
            );
            const ingredientValues = [];
            for (let i = 1; i < matchedIngredientTableRow.values.length; i++) {
              ingredientValues.push(matchedIngredientTableRow.values[i]);
            }

            const layerCustomValues = [];
            let valuesIndex = 0;
            for (let i = 0; i < layerRows; i++) {
              const layerRow = [];

              for (let j = 0; j < layerColumns; j++) {
                let ingredientValue = 0;
                ingredientValue = ingredientValues[valuesIndex]
                  ? Number(ingredientValues[valuesIndex])
                  : 0;

                if (
                  tableIngredient.matchedIngredient.selectedStock.concentrationUnits ==
                  'M'
                ) {
                  if (tableIngredient.selectedUnit.name == 'mM') {
                    ingredientValue = ingredientValue / 1000;
                  }
                }
                if (
                  tableIngredient.matchedIngredient.selectedStock.concentrationUnits ==
                  'mM'
                ) {
                  if (tableIngredient.selectedUnit.name == 'M') {
                    ingredientValue = ingredientValue * 1000;
                  }
                }
                layerRow.push(ingredientValue);
                valuesIndex = valuesIndex + 1;
              }

              layerCustomValues.push(layerRow);
            }

            let phCustomValues = [];
            let concCustomValues = [];

            if (tableIngredient.selectedUnit.name == 'pH') {
              phCustomValues = layerCustomValues;
            } else {
              concCustomValues = layerCustomValues;
            }

            gridIngredient.concentrationVaryAlongCustom =
              new ConcentrationVaryAlongCustomDTO();
            gridIngredient.concentrationVaryAlongCustom.id = getNewId();
            gridIngredient.concentrationVaryAlongCustom.concentrationVaryAlongCustomValues =
              concCustomValues;

            gridIngredient.pHVaryAlongCustom = new PhVaryAlongCustomDTO();
            gridIngredient.pHVaryAlongCustom.id = getNewId();
            gridIngredient.pHVaryAlongCustom.pHVaryAlongCustomValues = phCustomValues;
            gridDto.gridIngredient.push(gridIngredient);
          }
        } else {
          if (tableIngredient.selectedUnit.name != 'pH') {
            if (!ing.concentrationVaryAlongCustom.length) {
              const layerColumns = _selectedLayer.width + 1;
              const layerRows = _selectedLayer.height + 1;
              const ingredientValues = [];
              for (let i = 1; i < tableIngredient.values.length; i++) {
                ingredientValues.push(tableIngredient.values[i]);
              }

              const layerCustomValues = [];
              let valuesIndex = 0;
              for (let i = 0; i < layerRows; i++) {
                const layerRow = [];

                for (let j = 0; j < layerColumns; j++) {
                  let ingredientValue = 0;
                  ingredientValue = ingredientValues[valuesIndex]
                    ? Number(ingredientValues[valuesIndex])
                    : 0;

                  if (
                    tableIngredient.matchedIngredient.selectedStock.concentrationUnits ==
                    'M'
                  ) {
                    if (tableIngredient.selectedUnit.name == 'mM') {
                      ingredientValue = ingredientValue / 1000;
                    }
                  }
                  if (
                    tableIngredient.matchedIngredient.selectedStock.concentrationUnits ==
                    'mM'
                  ) {
                    if (tableIngredient.selectedUnit.name == 'M') {
                      ingredientValue = ingredientValue * 1000;
                    }
                  }
                  layerRow.push(ingredientValue);
                  valuesIndex = valuesIndex + 1;
                }

                layerCustomValues.push(layerRow);
              }

              ing.concentrationVaryAlongCustom.concentrationVaryAlongCustomValues =
                layerCustomValues;
            }
          }
        }
      }
    }

    /// Mark all linked layers dirty
    _layers.map((lyr) => {
      if (
        lyr.type == LayerTypeDTO.Grid &&
        (lyr.layerDetail as GridLayerDTO).gridId ==
          (_selectedLayer.layerDetail as GridLayerDTO).gridId
      ) {
        lyr.dirty = true;
      }
    });

    const tempSelectedLyrIndex = _layers.findIndex((lyr) => lyr.id === _selectedLayerId);
    _layers[tempSelectedLyrIndex].dirty = true;
    _layers[tempSelectedLyrIndex].layerDetail = selectedLayerDetails;

    _layers.map((lyr) =>
      dirtifyOverLappingLayers(lyr as DrawingLayer, _selectedLayer as DrawingLayer, true),
    );
    expData.layers = [...expData.layers];
  }
};

export const getCompositeImageKeysAsString = (
  wellNavigationDetails: WellNavigationDetails,
  DropImageParameters: DropImageParameters,
): string => {
  if (!wellNavigationDetails || !DropImageParameters) {
    return '';
  }
  const imageBatchId = DropImageParameters?.inspection?.groupedInspections?.find(
    (grp) => grp.plateId === wellNavigationDetails?.plateID,
  )?.imageBatchId;

  return `${wellNavigationDetails.plateID}-${DropImageParameters.dropNumber}-${DropImageParameters.regionName}-${DropImageParameters.cpId}-${imageBatchId}`;
};

export const getScoresOfWells = (
  wellDetails: { wellNumber: number; plateId: number; dropNumber: number }[],
  plates: Plate[],
) => {
  const plateIds = wellDetails.map((detail) => detail.plateId);
  const allPlatesScores = plates
    ?.filter((plate) => plateIds.includes(plate.plateId) && plate.plateScores)
    ?.map((plate) => plate.plateScores)
    .flat();
  if (allPlatesScores?.length > 0) {
    const scoresForWells = wellDetails.map((detail) => {
      return (
        allPlatesScores.find((scores) =>
          scores?.length > 0
            ? scores.some(
                (score) =>
                  score.wellNumber === detail.wellNumber &&
                  score.dropNumber === detail.dropNumber &&
                  score.plateId === detail.plateId,
              )
            : false,
        ) || []
      );
    });
    return scoresForWells;
  }
  return [];
};
